import Axhelper from '@/lib/Axhelper'
import ProjectsService from '@/services/ProjectsService'

const getRelationId = value => {
	if (!value) return ''
	if (typeof value === 'object') {
		return `${value?._id || value?.id || ''}`.trim()
	}
	return `${value}`.trim()
}

const normalizeProjectFiles = files => (
	Array.isArray(files) ? files : []
).map(item => ({
	fileUuid: `${item?.fileUuid || item?._id || ''}`.trim(),
	filePath: `${item?.filePath || ''}`.trim(),
	originalName: `${item?.originalName || item?.fileName || item?.name || ''}`.trim(),
})).filter(item => item.fileUuid)

const parseCertificates = value => {
	if (Array.isArray(value)) {
		return value
			.map(item => ({
				certificateId: `${item?.certificateId ?? item?.certificationId ?? item?._id ?? ''}`.trim(),
				certificateName: `${item?.certificateName ?? item?.name ?? item?.nazwa ?? ''}`.trim(),
			}))
			.filter(item => item.certificateId)
	}

	if (typeof value === 'string' && value.trim()) {
		try {
			return parseCertificates(JSON.parse(value))
		} catch {
			return []
		}
	}

	return []
}

const mapStep = step => {
	if (!step || typeof step !== 'object') return step

	const embedded = step?.placowkaId && typeof step.placowkaId === 'object'
		? step.placowkaId
		: null
	const certificates = parseCertificates(step.certificates)
	const requiredCertificationIds = Array.isArray(step.requiredCertificationIds) && step.requiredCertificationIds.length
		? step.requiredCertificationIds.map(id => `${id}`.trim()).filter(Boolean)
		: certificates.map(item => item.certificateId)
	const adres = `${step.adres ?? step.ulica ?? ''}`.trim()
	const miasto = `${step.miasto ?? ''}`.trim()
	const kod_pocztowy = `${step.kod_pocztowy || ''}`.trim()
	const locationParts = [adres, miasto, kod_pocztowy].filter(Boolean)
	const pozycje = (Array.isArray(step.pozycje) ? step.pozycje : []).map(item => ({
		...item,
		value: `${item?.value ?? ''}`.trim(),
		start_time: `${item?.start_time ?? item?.startTime ?? ''}`.trim(),
		end_time: `${item?.end_time ?? item?.endTime ?? ''}`.trim(),
		trainerId: item?.trainerId || null,
		trainerName: `${item?.trainerName || ''}`.trim(),
		uwagi: `${item?.uwagi ?? ''}`.trim(),
	}))
	const trainerNames = pozycje.map(item => item.trainerName).filter(Boolean)

	return {
		...step,
		_id: step._id || step.id || null,
		nazwa: step.nazwa || '',
		placowkaId: getRelationId(step.placowkaId) || null,
		placowkaName: `${step.placowkaName || embedded?.nazwa || ''}`.trim(),
		placowkaAddress: `${step.placowkaAddress || embedded?.adres || ''}`.trim(),
		certificates,
		requiredCertificationIds,
		data_rozpoczecia: step.data_rozpoczecia || '',
		data_zakonczenia: step.data_zakonczenia || step.data_rozpoczecia || '',
		startTime: step.startTime || step.godzina_rozpoczecia || '',
		endTime: step.endTime || step.godzina_zakonczenia || '',
		godzina_rozpoczecia: step.godzina_rozpoczecia || step.startTime || '',
		godzina_zakonczenia: step.godzina_zakonczenia || step.endTime || '',
		ulica: adres,
		adres,
		miasto,
		kod_pocztowy,
		location: step.location || locationParts.join(', '),
		g_lat: step.g_lat ?? '',
		g_long: step.g_long ?? '',
		status: step.status || 'new',
		notatki: step.notatki || '',
		osoba_kontaktowa: {
			nazwisko: `${step.osoba_kontaktowa?.nazwisko ?? ''}`.trim(),
			funkcja: `${step.osoba_kontaktowa?.funkcja ?? ''}`.trim(),
			telefon: `${step.osoba_kontaktowa?.telefon ?? ''}`.trim(),
		},
		pozycje,
		trainerNames,
	}
}

const mapProject = project => {
	if (!project || typeof project !== 'object') return project

	const steps = (Array.isArray(project.steps) ? project.steps : []).map(mapStep)
	const projectFields = { ...project }
	delete projectFields.data_rozpoczecia

	return {
		...projectFields,
		nazwa: project.nazwa || '',
		status: project.status || '',
		opis: project.opis || '',
		steps,
		stepsCount: steps.length,
		projectFiles: normalizeProjectFiles(project.projectfiles || project.projectFiles),
	}
}

class MojeProjektyService {
	constructor() {
		this.endpoints = {
			myProjects: '/api/projekty/myprojects',
			myDetails: '/api/projekty/mydetails',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = 'created',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.myProjects}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('MojeProjektyService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch my projects',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = `${searchTerm || ''}`.trim()
			if (!trimmed) {
				return { data: [], error: 'Invalid search term provided', totalCount: 0, hasMore: false }
			}

			const {
				limit = 50,
				sortColumn = 'created',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.myProjects}?searchText=${encodeURIComponent(trimmed)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('MojeProjektyService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search my projects',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(projectId) {
		try {
			if (!projectId) {
				return { data: null, error: 'Invalid project ID provided' }
			}

			const url = `${this.endpoints.myDetails}/${encodeURIComponent(projectId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: null, error: response.error }
			}

			return { data: mapProject(response.data), error: null }
		} catch (error) {
			console.error('MojeProjektyService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch project',
			}
		}
	}

	async uploadProjectFile(projectId, file, onUploadProgress) {
		return ProjectsService.uploadProjectFile(projectId, file, onUploadProgress)
	}

	async downloadProjectFile(projectId, fileUuid) {
		return ProjectsService.downloadProjectFile(projectId, fileUuid)
	}

	async deleteProjectFile(projectId, fileUuid) {
		return ProjectsService.deleteProjectFile(projectId, fileUuid)
	}

	_buildParams({ limit, sortColumn, sortDirection, filters }) {
		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		if (filters?.status) {
			params.append('status', String(filters.status))
		}
		if (filters?.placowkaId) {
			params.append('placowkaId', String(filters.placowkaId))
		}
		if (filters?.trainerId) {
			params.append('trainerId', String(filters.trainerId))
		}

		return params
	}

	_buildListResponse(response) {
		const rows = (Array.isArray(response.data) ? response.data : []).map(mapProject)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}
}

export default new MojeProjektyService()
